'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { Loader2, Calendar, Users, Target, MessageSquare, History, FolderKanban } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { GlassCard } from '@/components/ui/glass-card'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { SectionHeader } from '@/components/ui/section-header'
import { PageShell } from '@/components/ui/page-shell'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { cn } from '@/lib/utils'

type CardTone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'
const toCardTone = (t: ReturnType<typeof statusTone>): CardTone => (t === 'neutral' ? 'default' : t)

interface User {
  id: string
  name: string
  employeeId: string | null
}

interface ProjectAssignment {
  user: User
  role: string
}

interface ProgressUpdate {
  id: string
  updateText: string
  progressPercentage: number | null
  createdAt: string
  user: {
    id: string
    name: string
    employeeId: string | null
  }
}

interface Project {
  id: string
  name: string
  description: string | null
  status: string
  priority: string
  startDate: string | null
  endDate: string | null
  progress: number
  assignments: ProjectAssignment[]
}

function priorityTone(p: string): 'red' | 'orange' | 'amber' | 'neutral' {
  if (p === 'CRITICAL') return 'red'
  if (p === 'HIGH') return 'orange'
  if (p === 'MEDIUM') return 'amber'
  return 'neutral'
}

export function EmployeeProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [updateText, setUpdateText] = useState('')
  const [suggestedProgress, setSuggestedProgress] = useState<number>(0)
  const [submitting, setSubmitting] = useState(false)
  const [progressHistory, setProgressHistory] = useState<ProgressUpdate[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/projects')
      const data = await res.json()
      if (data.projects) setProjects(data.projects)
    } catch (error) {
      console.error('Error fetching projects:', error)
      toast.error('Failed to fetch projects')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openUpdateDialog = (project: Project) => {
    setSelectedProject(project)
    setUpdateText('')
    setSuggestedProgress(project.progress)
    setUpdateDialogOpen(true)
  }

  const openHistoryDialog = async (project: Project) => {
    setSelectedProject(project)
    setHistoryDialogOpen(true)
    setLoadingHistory(true)
    try {
      const res = await apiFetch(`/api/projects/${project.id}/progress`)
      const data = await res.json()
      if (data.progressUpdates) setProgressHistory(data.progressUpdates)
    } catch (error) {
      console.error('Error fetching progress history:', error)
      toast.error('Failed to fetch progress history')
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleProgressUpdate = async () => {
    if (!selectedProject) return
    if (!updateText.trim()) {
      toast.error('Please enter a progress update')
      return
    }

    setSubmitting(true)
    try {
      const res = await apiFetch(`/api/projects/${selectedProject.id}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updateText: updateText.trim(),
          progressPercentage: suggestedProgress
        })
      })
      toast.success('Progress update added successfully')
      fetchData()
      setUpdateDialogOpen(false)
      setUpdateText('')
    } catch (e: any) {
      toast.error(e.message || 'Failed to add progress update')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageShell>
      <SectionHeader
        icon={FolderKanban}
        title='My Projects'
        description='View and update your assigned projects'
      />

      {loading ? (
        <TabLoader />
      ) : projects.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={Target}
            title='No projects assigned yet'
            description='Projects you are assigned to will appear here.'
          />
        </GlassCard>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4'>
          {projects.map((project) => {
            const tone = statusTone(project.status)
            return (
              <GlassCard key={project.id} tone={toCardTone(tone)} interactive>
                <div className='space-y-4'>
                  <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0 flex-1'>
                      <h3 className='text-base font-semibold text-neutral-900 dark:text-white/90 tracking-tight truncate'>{project.name}</h3>
                      <p className='text-xs text-neutral-500 dark:text-white/50 line-clamp-2 mt-1'>{project.description || 'No description'}</p>
                    </div>
                  </div>

                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <StatusPill tone={tone}>{project.status.replace('_', ' ')}</StatusPill>
                    <StatusPill tone={priorityTone(project.priority)}>{project.priority}</StatusPill>
                  </div>

                  <div className='space-y-1.5'>
                    <div className='flex items-center justify-between text-xs'>
                      <span className='text-neutral-500 dark:text-white/50'>Progress</span>
                      <span className='text-neutral-900 dark:text-white/90 font-medium tabular-nums'>{project.progress}%</span>
                    </div>
                    <Progress value={project.progress} className='h-2' />
                  </div>

                  {(project.startDate || project.endDate) && (
                    <div className='flex items-center gap-1.5 text-[11px] text-neutral-400 dark:text-white/40 flex-wrap'>
                      <Calendar className='h-3 w-3' />
                      {project.startDate && (
                        <span className='tabular-nums'>{(() => { const d = new Date(project.startDate + 'T00:00:00'); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}</span>
                      )}
                      {project.endDate && (
                        <span className='tabular-nums'>→ {(() => { const d = new Date(project.endDate + 'T00:00:00'); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}</span>
                      )}
                    </div>
                  )}

                  <div className='flex items-center gap-2'>
                    <Users className='h-3.5 w-3.5 text-neutral-400 dark:text-white/40' />
                    <div className='flex -space-x-2'>
                      {project.assignments.slice(0, 4).map((assignment, ai) => (
                        <div
                          key={assignment.user?.id ?? ai}
                          className='h-7 w-7 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 ring-2 ring-white dark:ring-neutral-950 flex items-center justify-center text-[11px] text-white font-medium'
                          title={assignment.user?.name || 'Unknown'}
                        >
                          {(assignment.user?.name || 'U').charAt(0).toUpperCase()}
                        </div>
                      ))}
                      {project.assignments.length > 4 && (
                        <div className='h-7 w-7 rounded-full bg-neutral-100 dark:bg-white/10 ring-2 ring-white dark:ring-neutral-950 flex items-center justify-center text-[10px] text-neutral-800 dark:text-white/80 font-medium'>
                          +{project.assignments.length - 4}
                        </div>
                      )}
                    </div>
                    <span className='text-[11px] text-neutral-400 dark:text-white/40 ml-1'>
                      {project.assignments.length} member{project.assignments.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {project.status !== 'COMPLETED' && project.status !== 'CANCELLED' && (
                    <div className='flex gap-2 pt-1'>
                      <Button
                        size='sm'
                        onClick={() => openUpdateDialog(project)}
                        className={cn(PRIMARY_BTN, 'flex-1')}
                      >
                        <MessageSquare className='h-4 w-4 mr-1' />
                        Update
                      </Button>
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => openHistoryDialog(project)}
                        className='border-neutral-200 dark:border-white/15 bg-neutral-50 dark:bg-white/5 text-neutral-800 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/10'
                      >
                        <History className='h-4 w-4' />
                      </Button>
                    </div>
                  )}
                </div>
              </GlassCard>
            )
          })}
        </div>
      )}

      {/* Progress Update Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className='bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10'>
          <DialogHeader>
            <DialogTitle className='text-neutral-900 dark:text-white'>Update Progress — {selectedProject?.name}</DialogTitle>
            <DialogDescription className='text-neutral-500 dark:text-white/50'>
              Describe what you&apos;ve accomplished and the current status.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label>Progress update *</Label>
              <Textarea
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                placeholder="What's done, what's next, blockers..."
                className='min-h-[120px]'
              />
            </div>
            <div className='space-y-2'>
              <Label>Suggested progress (%)</Label>
              <div className='flex items-center gap-4'>
                <input
                  type='range'
                  min='0'
                  max='100'
                  value={suggestedProgress}
                  onChange={(e) => setSuggestedProgress(parseInt(e.target.value))}
                  className='flex-1 h-2 bg-neutral-200 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-orange-500'
                />
                <span className='text-neutral-900 dark:text-white font-medium w-12 text-right tabular-nums'>{suggestedProgress}%</span>
              </div>
              <p className='text-xs text-neutral-400 dark:text-white/40'>This will update the overall project progress</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setUpdateDialogOpen(false)} className='border-neutral-200 dark:border-white/15 bg-neutral-50 dark:bg-white/5 text-neutral-800 dark:text-white/80'>
              Cancel
            </Button>
            <Button
              onClick={handleProgressUpdate}
              disabled={submitting || !updateText.trim()}
              className={PRIMARY_BTN}
            >
              {submitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Submitting...
                </>
              ) : (
                'Submit Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className='max-w-2xl bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10'>
          <DialogHeader>
            <DialogTitle className='text-neutral-900 dark:text-white'>Progress History — {selectedProject?.name}</DialogTitle>
            <DialogDescription className='text-neutral-500 dark:text-white/50'>
              All progress updates for this project.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className='h-[400px]'>
            {loadingHistory ? (
              <TabLoader />
            ) : progressHistory.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title='No progress updates yet'
                description='Updates posted against this project will show up here.'
              />
            ) : (
              <div className='space-y-3 pr-4'>
                {progressHistory.map((update) => (
                  <div key={update.id} className='rounded-xl p-3.5 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5'>
                    <div className='flex items-start justify-between gap-2 mb-2'>
                      <div className='flex items-center gap-2 min-w-0'>
                        <div className='h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-xs text-white font-medium shrink-0'>
                          {(update.user?.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className='min-w-0'>
                          <p className='text-sm text-neutral-900 dark:text-white/90 font-medium truncate'>{update.user?.name || 'Unknown'}</p>
                          <p className='text-[11px] text-neutral-400 dark:text-white/40'>{update.user?.employeeId || '-'}</p>
                        </div>
                      </div>
                      <div className='text-right shrink-0'>
                        <p className='text-[11px] text-neutral-400 dark:text-white/40 tabular-nums'>
                          {(() => { const d = new Date(update.createdAt); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` })()}
                        </p>
                        {update.progressPercentage !== null && update.progressPercentage > 0 && (
                          <StatusPill tone='orange' className='mt-1'>{update.progressPercentage}%</StatusPill>
                        )}
                      </div>
                    </div>
                    <p className='text-sm text-neutral-700 dark:text-white/70 whitespace-pre-wrap'>{update.updateText}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </PageShell>
  )
}
